import { useState } from 'react'
import { type InviteCreateResponse } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'

interface Props {
  result: InviteCreateResponse
  onClose: () => void
}

export function InviteCreatedModal({ result, onClose }: Props) {
  const [copied, setCopied] = useState<string | null>(null)

  const handleCopyToken = async (token: string) => {
    await navigator.clipboard.writeText(token)
    setCopied(token)
    setTimeout(() => setCopied(null), 2000)
  }

  const handleCopyAll = async () => {
    const text = result.invites.map((inv) => inv.token).join('\n')
    await navigator.clipboard.writeText(text)
    setCopied('all')
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">Invites Created</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {result.count} invite(s) created. Copy the tokens below — they won't be shown again.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-700 dark:text-amber-300">
          These tokens are shown only once. Make sure to copy them before closing.
        </div>

        <div className="space-y-2 mb-4">
          {result.invites.map((invite) => (
            <div
              key={invite.id}
              className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#0f0d12] p-3"
            >
              <div className="min-w-0 flex-1">
                {invite.email && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">{invite.email}</p>
                )}
                <p className="font-mono text-sm text-gray-900 dark:text-gray-100 truncate">{invite.token}</p>
              </div>
              <button
                onClick={() => handleCopyToken(invite.token)}
                className="ml-3 flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-[#503864] dark:text-[#b58fcc] hover:bg-[#f9f4ff] dark:hover:bg-[#2d2236] transition-colors"
              >
                {copied === invite.token ? 'Copied!' : 'Copy'}
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <CustomButton onClick={handleCopyAll} variant="secondary">
            {copied === 'all' ? 'Copied All!' : 'Copy All Tokens'}
          </CustomButton>
          <CustomButton onClick={onClose} variant="primary">
            Done
          </CustomButton>
        </div>
      </div>
    </div>
  )
}
